import Link from 'next/link'

import ENV_VARS from '@/config'
import {
  ArrowMoreOptionsIcon,
  BellIcon,
  ChartIcon,
  CloseButtonGrey96,
  DropdownChatIcon,
  DropdownFavouritesIcon,
  DropdownListingsIcon,
  DropdownLogoutIcon,
  DropdownPricingIcon,
  DropdownProfileIcon,
  DropdownSettingsIcon,
  DropdownTeamDisabledIcon,
  DropdownTeamIcon,
  DropdownTenantsIcon,
  FileAltIcon
} from '@/components/icons'

import BackButton from './back-button'
import DropdownLink from './dropdown-link'
import SidebarLinkMobile from './sidebar-link-mobile'
import {
  CloseButton,
  DropdownButton,
  DropdownLabel,
  PricingLink,
  PricingLinkMobile,
  SidebarButtonMobile,
  SidebarHeader,
  SidebarLabelMobile,
  StyledChatNotification,
  SubMenuTitle
} from './simple-components'

const { DASHBOARD_URL } = ENV_VARS

const renderPricingOption = (t, trackingId = '') => {
  if (trackingId === 'logged-') {
    return (
      <DropdownLink
        id="menu-manage-logged-pricing"
        href="/pricing"
        leftIcon={<DropdownPricingIcon width={16} height={16} />}
        title={t('b.header.view.pricing.button', 'Pricing')}
      />
    )
  }
  return (
    <Link href="/pricing" passHref legacyBehavior>
      <PricingLink target="_self" id="menu-manage-pricing">
        {t('b.header.view.pricing.button', 'Pricing')}
        <span>{t('For Landlords', 'For Landlords')}</span>
      </PricingLink>
    </Link>
  )
}

const renderLoginOption = (t, loginClick) => {
  return (
    <DropdownButton onClick={loginClick} id="menu-account-login">
      {t('common.login', 'Log in')}
    </DropdownButton>
  )
}

const renderSignupOption = (t, signupClick) => {
  return (
    <DropdownButton onClick={signupClick} id="menu-account-signup">
      {t('common.sign_up', 'Sign up')}
    </DropdownButton>
  )
}

const renderProfileOption = t => {
  return (
    <DropdownLink
      id="menu-account-logged-profile"
      href={`${DASHBOARD_URL}/profile`}
      leftIcon={<DropdownProfileIcon width={16} height={16} />}
      title={t('common.profile', 'Profile')}
    />
  )
}

const renderProfileOptionMobile = t => {
  return (
    <SidebarLinkMobile
      id="menu-account-logged-profile"
      target="_self"
      href={`${DASHBOARD_URL}/profile`}
      leftIcon={<DropdownProfileIcon width={20} height={20} />}
      title={t('common.profile', 'Profile')}
    />
  )
}

const renderRentalsOption = (t, trackingId = '') => {
  return (
    <DropdownLink
      id={`menu-manage-${trackingId}rentals`}
      href={`${DASHBOARD_URL}/rentals`}
      leftIcon={<DropdownListingsIcon width={16} height={16} />}
      title={t('b.header.view.rentals.link', 'Your Rentals')}
    />
  )
}

const renderRentalsOptionMobile = (t, trackingId = '') => {
  return (
    <SidebarLinkMobile
      id={`menu-manage-${trackingId}rentals`}
      target="_self"
      href={`${DASHBOARD_URL}/rentals`}
      leftIcon={<DropdownListingsIcon width={20} height={20} />}
      title={t('b.header.view.rentals.link', 'Your Rentals')}
    />
  )
}

const renderTenantsOption = (t, trackingId = '') => {
  return (
    <DropdownLink
      id={`menu-manage-${trackingId}tenants`}
      href={`${DASHBOARD_URL}/tenants`}
      leftIcon={<DropdownTenantsIcon width={16} height={16} />}
      title={t('b.header.view.rented.link', 'Rented')}
    />
  )
}

const renderTenantsOptionMobile = (t, trackingId = '') => {
  return (
    <SidebarLinkMobile
      id={`menu-manage-${trackingId}tenants`}
      target="_self"
      href={`${DASHBOARD_URL}/tenants`}
      leftIcon={<DropdownTenantsIcon width={20} height={20} />}
      title={t('b.header.view.rented.link', 'Rented')}
    />
  )
}

const renderCreditUsageOption = (t, trackingId = '') => {
  return (
    <DropdownLink
      id={`menu-manage-${trackingId}usage-credits`}
      href={`${DASHBOARD_URL}/usage-credits/products/my-products`}
      leftIcon={<ChartIcon width={16} height={16} />}
      title={t('b.header.view.credit_usage.link', 'Credit Usage')}
    />
  )
}

const renderCreditUsageOptionMobile = (t, trackingId = '') => {
  return (
    <SidebarLinkMobile
      id={`menu-manage-${trackingId}usage-credits`}
      target="_self"
      href={`${DASHBOARD_URL}/usage-credits/products/my-products`}
      leftIcon={<ChartIcon width={20} height={20} />}
      title={t('b.header.view.credit_usage.link', 'Credit Usage')}
    />
  )
}

const renderSavedAlertsOption = (t, handleClick, isLoggedIn = false) => {
  return isLoggedIn ? (
    <DropdownLink
      id={`saved-alerts-menu-click`}
      href={`/saved-alerts`}
      title={t('b.header.view.saved_alerts.link', 'Saved Alerts')}
      leftIcon={<BellIcon width={16} height={16} />}
    />
  ) : (
    <DropdownButton id={`saved-alerts-menu-click`} onClick={handleClick}>
      <BellIcon width={16} height={16} />
      {t('b.header.view.saved_alerts.link', 'Saved Alerts')}
    </DropdownButton>
  )
}

const renderTeamOption = (t, hasEssentialsPlan) => {
  return (
    <DropdownLink
      id="menu-manage-logged-team"
      title={t('b.header.view.team.link', 'Team')}
      hasEssentialsPlan={hasEssentialsPlan}
      leftIcon={
        hasEssentialsPlan ? (
          <DropdownTeamDisabledIcon width={16} height={16} />
        ) : (
          <DropdownTeamIcon width={16} height={16} />
        )
      }
      href={hasEssentialsPlan ? '/pricing' : `${DASHBOARD_URL}/manage/users`}
    />
  )
}

const renderTeamOptionMobile = (t, hasEssentialsPlan) => {
  return (
    <SidebarLinkMobile
      id="menu-manage-logged-team"
      title={t('b.header.view.team.link', 'Team')}
      hasEssentialsPlan={hasEssentialsPlan}
      leftIcon={
        hasEssentialsPlan ? (
          <DropdownTeamDisabledIcon width={20} height={20} />
        ) : (
          <DropdownTeamIcon width={20} height={20} />
        )
      }
      target="_self"
      href={hasEssentialsPlan ? '/pricing' : `${DASHBOARD_URL}/manage/users`}
    />
  )
}

const renderLandlordSettingsOption = t => {
  return (
    <DropdownLink
      id="menu-account-logged-settings"
      href={`${DASHBOARD_URL}/settings/plan-settings`}
      leftIcon={<DropdownSettingsIcon width={16} height={16} />}
      title={t('b.header.view.settings.button', 'Settings')}
    />
  )
}
const renderTenantSettingsOption = t => {
  return (
    <DropdownLink
      id="menu-account-logged-settings"
      href={`${DASHBOARD_URL}/settings/payment-settings`}
      leftIcon={<DropdownSettingsIcon width={16} height={16} />}
      title={t('b.header.view.settings.button', 'Settings')}
    />
  )
}

const renderSettingsOptionMobile = t => {
  return (
    <SidebarLinkMobile
      id="menu-account-logged-settings"
      target="_self"
      href={`${DASHBOARD_URL}/settings`}
      leftIcon={<DropdownSettingsIcon width={20} height={20} />}
      title={t('b.header.view.settings.button', 'Settings')}
    />
  )
}

const renderChatOption = (t, trackingId = '', onClick) => {
  return (
    <DropdownLink
      id={`menu-manage-${trackingId}chat`}
      href={`${DASHBOARD_URL}/chat`}
      leftIcon={<DropdownChatIcon width={16} height={16} />}
      title={t('b.header.view.chat.button', 'Chat')}
      onClick={onClick}
    >
      <StyledChatNotification top="-8px" right="-28px" />
    </DropdownLink>
  )
}

const renderChatOptionMobile = (t, trackingId = '', onClick) => {
  return (
    <SidebarLinkMobile
      id={`menu-manage-${trackingId}chat`}
      target="_self"
      href={`${DASHBOARD_URL}/chat`}
      leftIcon={<DropdownChatIcon width={20} height={20} />}
      title={t('b.header.view.chat.button', 'Chat')}
      onClick={onClick}
    >
      <StyledChatNotification top="-8px" right="-28px" />
    </SidebarLinkMobile>
  )
}

const renderListingsOption = (t, trackingId = '', onClick) => {
  return (
    <DropdownLink
      id={`menu-manage-${trackingId}listings`}
      href={`${DASHBOARD_URL}/listings`}
      leftIcon={<DropdownListingsIcon width={16} height={16} />}
      title={t('b.header.view.listings.link', 'Listings')}
      onClick={onClick}
    />
  )
}

const renderListingsOptionMobile = (t, trackingId = '', onClick) => {
  return (
    <SidebarLinkMobile
      id={`menu-manage-${trackingId}listings`}
      target="_self"
      href={`${DASHBOARD_URL}/listings`}
      leftIcon={<DropdownListingsIcon width={20} height={20} />}
      title={t('b.header.view.listings.link', 'Listings')}
      onClick={onClick}
    />
  )
}

const renderTrustScoreOption = (t, trackingId = '') => {
  return (
    <DropdownLink
      id={`menu-manage-${trackingId}trustscore`}
      href={`${DASHBOARD_URL}/trust-score`}
      leftIcon={<FileAltIcon width={16} height={16} />}
      title={t('b.header.view.trustscore.link', 'Trust Score')}
    />
  )
}

const renderTrustScoreOptionMobile = (t, trackingId = '') => {
  return (
    <SidebarLinkMobile
      id={`menu-manage-${trackingId}trustscore`}
      target="_self"
      href={`${DASHBOARD_URL}/trust-score`}
      leftIcon={<FileAltIcon width={20} height={20} />}
      title={t('b.header.view.trustscore.link', 'Trust Score')}
    />
  )
}

const renderFavouritesOption = (t, trackingId = '') => {
  return (
    <DropdownLink
      id={`menu-manage-${trackingId}favourites`}
      href="/favourites"
      leftIcon={<DropdownFavouritesIcon width={16} height={16} />}
      title={t('b.header.view.favourites.button', 'Favourites')}
    />
  )
}

const renderFavouritesOptionMobile = (t, trackingId = '') => {
  return (
    <SidebarLinkMobile
      id={`menu-manage-${trackingId}favourites`}
      target="_self"
      href="/favourites"
      leftIcon={<DropdownFavouritesIcon width={20} height={20} />}
      title={t('b.header.view.favourites.button', 'Favourites')}
    />
  )
}

const renderLogoutOption = (t, logoutClick) => {
  return (
    <DropdownButton onClick={logoutClick} id="menu-account-logged-logout">
      <DropdownLogoutIcon width={16} height={16} />
      {t('b.header.view.log_out.button', 'Logout')}
    </DropdownButton>
  )
}

const renderLogoutOptionMobile = (t, logoutClick) => {
  return (
    <SidebarButtonMobile
      onClick={logoutClick}
      hasLeftIcon
      id="menu-account-logged-logout"
    >
      <DropdownLogoutIcon width={20} height={20} />
      {t('b.header.view.log_out.button', 'Logout')}
    </SidebarButtonMobile>
  )
}
const renderSavedAlertsOptionMobile = (t, handleClick, isLoggedIn = false) => {
  return isLoggedIn ? (
    <SidebarLinkMobile
      id={`saved-alerts-menu-click`}
      href={`/saved-alerts`}
      title={t('b.header.view.saved_alerts.link', 'Saved Alerts')}
      leftIcon={<BellIcon width={20} height={20} />}
    />
  ) : (
    <SidebarButtonMobile
      onClick={handleClick}
      id={`saved-alerts-menu-click`}
      hasLeftIcon={true}
    >
      <BellIcon width={20} height={20} />
      {t('b.header.view.saved_alerts.link', 'Saved Alerts')}
    </SidebarButtonMobile>
  )
}

const renderManageLabel = t => {
  return <DropdownLabel>{t('Manage', 'Manage')}</DropdownLabel>
}

const renderManageLabelMobile = t => {
  return <SidebarLabelMobile>{t('Manage', 'Manage')}</SidebarLabelMobile>
}

const renderAccountLabel = t => {
  return <DropdownLabel>{t('Account', 'Account')}</DropdownLabel>
}

const renderAccountLabelMobile = t => {
  return <SidebarLabelMobile>{t('Account', 'Account')}</SidebarLabelMobile>
}

const renderManageOptionMobile = (t, onClick) => {
  return (
    <SidebarButtonMobile onClick={onClick} id="menu-manage-section">
      {t('Manage', 'Manage')}
      <ArrowMoreOptionsIcon width={11} height={11} />
    </SidebarButtonMobile>
  )
}

const renderLandlordsOptionMobile = (t, onClick) => {
  return (
    <SidebarButtonMobile onClick={onClick} id="menu-landlords-section">
      {t('Landlords', 'Landlords')}
      <ArrowMoreOptionsIcon width={11} height={11} />
    </SidebarButtonMobile>
  )
}

const renderRentersOptionMobile = (t, onClick) => {
  return (
    <SidebarButtonMobile onClick={onClick} id="menu-renters-section">
      {t('Renters', 'Renters')}
      <ArrowMoreOptionsIcon width={11} height={11} />
    </SidebarButtonMobile>
  )
}

const renderResourcesOptionMobile = (t, onClick) => {
  return (
    <SidebarButtonMobile onClick={onClick} id="menu-resources-section">
      {t('Resources', 'Resources')}
      <ArrowMoreOptionsIcon width={11} height={11} />
    </SidebarButtonMobile>
  )
}

const renderPricingOptionMobile = t => {
  return (
    <Link href="/pricing" passHref legacyBehavior>
      <PricingLinkMobile target="_self" id="menu-manage-pricing">
        {t('b.header.view.pricing.button', 'Pricing')}
        <span>{t('For Landlords', 'For Landlords')}</span>
      </PricingLinkMobile>
    </Link>
  )
}

const renderPricingOptionLoggedMobile = t => {
  return (
    <SidebarLinkMobile
      id="menu-manage-logged-pricing"
      target="_self"
      href="/pricing"
      leftIcon={<DropdownPricingIcon width={20} height={20} />}
      title={t('b.header.view.pricing.button', 'Pricing')}
    />
  )
}

const renderManageSidebarHeader = (t, onBackClick, onCloseClick) => {
  return (
    <SidebarHeader>
      <BackButton onClick={onBackClick} />
      <SubMenuTitle>{t('Manage', 'Manage')}</SubMenuTitle>
      <CloseButton onClick={onCloseClick}>
        <CloseButtonGrey96 width={40} height={40} />
      </CloseButton>
    </SidebarHeader>
  )
}

const renderLandlordsSidebarHeader = (t, onBackClick, onCloseClick) => {
  return (
    <SidebarHeader>
      <BackButton onClick={onBackClick} />
      <SubMenuTitle>{t('Landlords', 'Landlords')}</SubMenuTitle>
      <CloseButton onClick={onCloseClick}>
        <CloseButtonGrey96 width={32} height={32} />
      </CloseButton>
    </SidebarHeader>
  )
}

const renderRentersSidebarHeader = (t, onBackClick, onCloseClick) => {
  return (
    <SidebarHeader>
      <BackButton onClick={onBackClick} />
      <SubMenuTitle>{t('Renters', 'Renters')}</SubMenuTitle>
      <CloseButton onClick={onCloseClick}>
        <CloseButtonGrey96 width={32} height={32} />
      </CloseButton>
    </SidebarHeader>
  )
}

const renderResourcesSidebarHeader = (t, onBackClick, onCloseClick) => {
  return (
    <SidebarHeader>
      <BackButton onClick={onBackClick} />
      <SubMenuTitle>{t('Resources', 'Resources')}</SubMenuTitle>
      <CloseButton onClick={onCloseClick}>
        <CloseButtonGrey96 width={32} height={32} />
      </CloseButton>
    </SidebarHeader>
  )
}

export {
  renderManageSidebarHeader,
  renderLandlordsSidebarHeader,
  renderRentersSidebarHeader,
  renderResourcesSidebarHeader,
  renderPricingOption,
  renderLoginOption,
  renderSignupOption,
  renderProfileOption,
  renderProfileOptionMobile,
  renderRentalsOption,
  renderRentalsOptionMobile,
  renderTenantsOption,
  renderTenantsOptionMobile,
  renderCreditUsageOption,
  renderCreditUsageOptionMobile,
  renderSavedAlertsOption,
  renderSavedAlertsOptionMobile,
  renderTeamOption,
  renderTeamOptionMobile,
  renderLandlordSettingsOption,
  renderTenantSettingsOption,
  renderSettingsOptionMobile,
  renderChatOption,
  renderChatOptionMobile,
  renderListingsOption,
  renderListingsOptionMobile,
  renderTrustScoreOption,
  renderTrustScoreOptionMobile,
  renderFavouritesOption,
  renderFavouritesOptionMobile,
  renderLogoutOption,
  renderLogoutOptionMobile,
  renderManageLabel,
  renderManageLabelMobile,
  renderAccountLabel,
  renderAccountLabelMobile,
  renderManageOptionMobile,
  renderLandlordsOptionMobile,
  renderRentersOptionMobile,
  renderResourcesOptionMobile,
  renderPricingOptionMobile,
  renderPricingOptionLoggedMobile
}
